import { Observable } from './observable';

/**
 * One asynchronously loaded value with its loading state. A newer `load()` or a `clear()` makes the answer of
 * an older request irrelevant, so data of a previous scope can never land after the scope changed.
 *
 * A load the service did not answer (an outage, `problem.outage`) stays said while it is read again: whoever says
 * the outage reads it again (`retry()`, on the page's one notice, decision D163) and the value appears in place once
 * the service answers, without a reload. What was read before stays with when it was read (`updated`) only while it
 * answers the question asked now (`kept`): rows of another filter or search are never shown as this one's. A refusal
 * is an answer: nothing reads it again by itself.
 */
export class Resource<Value> extends Observable {
	/** The resources whose last read waited on a session that ended and was not sent again */
	static #waiting = new Set<Resource<unknown>>();

	/**
	 * Reads again every resource whose last read was not sent because the session ended (a quiet problem) and that
	 * a view still shows: what the page's `Session` calls once the person is signed in again
	 */
	static resume() {
		const waiting = [...Resource.#waiting];
		Resource.#waiting.clear();
		for (const resource of waiting) resource.observed && void resource.retry();
	}

	#generation = 0;
	#fetcher: (() => Promise<Value>) | undefined;
	// What the last load asked for, and what the value held answers
	#asked = '';
	#held = '';

	#status: 'idle' | 'loading' | 'ready' | 'failed' = 'idle';
	get status() {
		return this.#status;
	}

	#value: Value | undefined;
	get value() {
		return this.#value;
	}

	#problem: unknown;
	/**
	 * What the last failed load threw
	 */
	get problem() {
		return this.#problem;
	}

	get loading() {
		return this.#status === 'loading';
	}

	/** Its first read has not been answered: no value yet and nothing failed, so a view waits in place (LOAD-01) */
	get pending() {
		return this.#value === undefined && this.#status !== 'failed' && this.#problem === undefined;
	}

	/** Whether the last load got no answer: the service, or a product it asked, did not answer */
	get outage() {
		return (this.#problem as { outage?: unknown } | undefined)?.outage === true;
	}

	#updated: number | undefined;
	/** When the value held was read, in milliseconds; undefined before the first answer */
	get updated() {
		return this.#updated;
	}

	/** Whether the value held answers the question asked now, so it can stay on screen while that question is unanswered */
	get kept() {
		return this.#value !== undefined && this.#held === this.#asked;
	}

	/**
	 * Keeps the current value visible while it reloads.
	 *
	 * @param basis What is asked (a filter, a search): a value read for another basis is never `kept` for this one
	 * @returns The loaded value, or undefined when the load failed or was superseded
	 */
	async load(fetcher: () => Promise<Value>, basis = ''): Promise<Value | undefined> {
		const generation = ++this.#generation;
		const same = basis === this.#asked;
		this.#fetcher = fetcher;
		this.#asked = basis;
		Resource.#waiting.delete(this as Resource<unknown>);
		this.#status = 'loading';
		// While the same question is read again after an outage, the outage stays what the view says
		if (!this.outage || !same) this.#problem = undefined;
		this.changed();

		try {
			const value = await fetcher();
			if (generation !== this.#generation) return;
			this.#keep(value);
			this.changed();
			return value;
		} catch (problem) {
			if (generation !== this.#generation) return;
			// A read the session's ending kept from being sent: what was on screen stays, and it is read again once
			// the person is signed in (`resume`); no screen shows it as a failure
			const quiet = (problem as { quiet?: boolean } | undefined)?.quiet === true;
			if (quiet) Resource.#waiting.add(this as Resource<unknown>);
			this.#problem = quiet && this.#value !== undefined ? undefined : problem;
			this.#status = quiet && this.#value !== undefined ? 'ready' : 'failed';
			this.changed();
		}
	}

	/**
	 * Reads again what the last load asked for: the page's outage notice calls it on its schedule and on Try again
	 *
	 * @returns Whether it got an answer; false only while the outage holds
	 */
	async retry(): Promise<boolean> {
		const fetcher = this.#fetcher;
		if (!fetcher) return true;
		await this.load(fetcher, this.#asked);
		return !this.outage;
	}

	/**
	 * Replaces the value, for an answer obtained another way, such as the result of a mutation
	 */
	set(value: Value) {
		this.#generation++;
		this.#keep(value);
		this.changed();
	}

	clear() {
		this.#generation++;
		this.#fetcher = undefined;
		Resource.#waiting.delete(this as Resource<unknown>);
		this.#value = undefined;
		this.#problem = undefined;
		this.#updated = undefined;
		this.#asked = this.#held = '';
		this.#status = 'idle';
		this.changed();
	}

	// An answer of the question asked now, read at this moment
	#keep(value: Value) {
		this.#value = value;
		this.#held = this.#asked;
		this.#updated = Date.now();
		this.#status = 'ready';
		this.#problem = undefined;
	}
}
